import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { useGetServiceabilityServiceTypesQuery } from '../../../redux/d2cApiSlice'
import { serviceMeta } from '../apiMap'
import { Pill } from './common'

// Shared group-label style — matches ChoiceCards' label (12.5 / 700 / text.primary) so the
// funnel's "Consignment value / Service level / Parcel size" read like every other choice group.
const grpLbl = { fontSize: 12.5, fontWeight: 700, color: 'text.primary', mb: 0.85 }

/**
 * Step-1 service picker, re-shaped as the guided funnel from the freight-commercials scoping
 * (Direction → Consignment value → Service level / Handling → Parcel size → resolves to codes)
 * instead of one flat tile grid.
 *
 * Drop-in for ServiceTypePicker: SAME props (services, onToggle, cardType, showErr) and SAME
 * output — the resolved `code`s are what Step 3 / the generate payload consume, so nothing
 * downstream changes. Options and axes come from the SAME live service_type_master the
 * `/serviceability-intel/servicetype/taxonomy` page renders, read from its real structured
 * fields (category incl. HIGH_VALUE/HYPERLOCAL, direction, mode, weightBand, requiresQc,
 * totalLinkedCount, active) — so the funnel mirrors the taxonomy exactly.
 *
 * Safety: non-transactable rows (0 linked couriers / inactive) are SHOWN (like the taxonomy)
 * but their options are DISABLED, and resolve() only ever emits transactable codes — so a
 * code the engine can't price never reaches the payload.
 */

function classify(st) {
  const code = st.code || ''
  const name = st.name || ''
  const meta = serviceMeta(code, name)
  const U = `${code} ${name}`.toUpperCase()
  const direction = st.direction === 'RVP' || st.category === 'REVERSE' || meta.isReverse ? 'REV' : 'FWD'
  const value = st.category === 'HIGH_VALUE' || /HIGH.?VALUE|(^|[\s_])HV([\s_]|$)/.test(U) ? 'HV' : 'STD'
  const size = (st.weightBand || '').toUpperCase() === 'LARGE' || /LARGE/.test(U) ? 'LARGE' : 'REG'
  const requiresQc = st.requiresQc != null ? !!st.requiresQc : (/(^|_)QC|WITH.*QC|QUALITY/.test(U) && !/NO.?QC/.test(U))
  const handling = /NO.?QC|WITHOUT/.test(U) ? 'No QC' : (requiresQc ? 'With QC' : 'No QC')
  const mode = (st.mode || '').toUpperCase()
  let level = 'Surface'
  if (st.category === 'HYPERLOCAL' || mode === 'HYPERLOCAL' || /HYPERLOCAL|SUB.?\d+\s*H|H2\s*TO\s*4/.test(U)) level = 'Hyperlocal'
  else if (mode === 'AIR' || meta.isAir) level = 'Air'
  else if (st.category === 'SDD_NDD' || meta.isSdd) {
    if (/INTERCITY/.test(U)) level = 'Intercity NDD'
    else if (/SAME.?CITY|SC.?NDD/.test(U)) level = 'Same-City NDD'
    else if (/SAME.?DAY|(^|[\s_])SDD/.test(U)) level = 'Same-Day (SDD)'
    else level = 'NDD'
  }
  const couriers = st.totalLinkedCount || 0
  return { direction, value, size, handling, level, couriers, name, transactable: couriers > 0 && st.active !== false }
}

const VALUE_LABEL = { STD: 'Standard', HV: 'High value' }
const SIZE_LABEL = { REG: 'Regular (up to 10 kg)', LARGE: 'Large (10–50 kg)' }
const tog = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v])
const uniq = (arr) => Array.from(new Set(arr))
const EMPTY_SEL = { dir: [], fVal: [], fLevel: [], fSize: [], rVal: [], rHand: [], rSize: [] }

// FunnelToggle — a compact multi-select PILL (Option C). Deliberately different from the
// pick-one ChoiceCards in "Commercial type": pill-shaped, checkmark when on, meta inline.
function FunnelChip({ on, disabled, label, meta, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer', font: 'inherit', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        border: '1.5px solid', borderRadius: '999px', px: 1.5, py: 0.7,
        borderColor: on ? 'primary.main' : 'divider',
        bgcolor: on ? '#EAF1FC' : 'background.paper',
        color: on ? '#0B5CD6' : 'text.secondary',
        fontSize: 12.5, fontWeight: on ? 700 : 600,
        opacity: disabled ? 0.45 : 1, transition: 'all .14s ease',
        '&:hover': disabled ? {} : { borderColor: 'primary.main', bgcolor: on ? '#E0ECFD' : '#F5F9FF' },
      }}
    >
      {on && <CheckIcon sx={{ fontSize: 14 }} />}
      {label}
      {meta && <Box component="span" sx={{ fontWeight: 500, color: on ? 'inherit' : 'text.disabled', opacity: on ? 0.75 : 1 }}>· {meta}</Box>}
    </Box>
  )
}

export default function ServiceSelectorFunnel({ services = [], onToggle, cardType = 'blended', showErr = false }) {
  const { data, isLoading, isError, refetch } = useGetServiceabilityServiceTypesQuery()

  // Load the FULL service-type taxonomy exactly as /serviceability-intel/servicetype/taxonomy
  // shows it — every active service type in the system, with NO cardType-based trimming. The
  // three funnel steps do all the narrowing; 0-courier rows stay visible but flagged
  // non-transactable (and can never resolve into the payload).
  const inScope = useMemo(() => {
    const raw = data?.responseObj?.serviceTypes || data?.data?.serviceTypes || data?.serviceTypes || []
    return (Array.isArray(raw) ? raw : []).filter((st) => st.active !== false && st.category && st.code)
  }, [data])

  const codeMeta = useMemo(() => {
    const m = {}
    inScope.forEach((st) => { m[st.code] = classify(st) })
    return m
  }, [inScope])

  const codesWhere = (pred) => inScope.filter((s) => pred(codeMeta[s.code])).map((s) => s.code)
  const anyTransactable = (codes) => codes.some((c) => codeMeta[c].transactable)

  const [sel, setSel] = useState(EMPTY_SEL)

  // Resolve funnel picks → concrete codes. Only TRANSACTABLE codes are emitted (0-courier /
  // inactive can never reach the payload), and each axis is required only when it offers a choice.
  const fwdSizes = useMemo(() => uniq(codesWhere((c) => c.direction === 'FWD').map((c) => codeMeta[c].size)), [inScope]) // eslint-disable-line react-hooks/exhaustive-deps
  const revSizes = useMemo(() => uniq(codesWhere((c) => c.direction === 'REV').map((c) => codeMeta[c].size)), [inScope]) // eslint-disable-line react-hooks/exhaustive-deps
  const fwdVals = useMemo(() => uniq(codesWhere((c) => c.direction === 'FWD').map((c) => codeMeta[c].value)), [inScope]) // eslint-disable-line react-hooks/exhaustive-deps
  const revVals = useMemo(() => uniq(codesWhere((c) => c.direction === 'REV').map((c) => codeMeta[c].value)), [inScope]) // eslint-disable-line react-hooks/exhaustive-deps

  const resolved = useMemo(() => {
    const out = []
    inScope.forEach((s) => {
      const cl = codeMeta[s.code]
      if (!cl.transactable) return
      if (!sel.dir.includes(cl.direction)) return
      if (cl.direction === 'FWD') {
        if (fwdVals.length > 1 && (!sel.fVal.length || !sel.fVal.includes(cl.value))) return
        if (!sel.fLevel.length || !sel.fLevel.includes(cl.level)) return
        if (fwdSizes.length > 1 && (!sel.fSize.length || !sel.fSize.includes(cl.size))) return
      } else {
        if (revVals.length > 1 && (!sel.rVal.length || !sel.rVal.includes(cl.value))) return
        if (!sel.rHand.length || !sel.rHand.includes(cl.handling)) return
        if (revSizes.length > 1 && (!sel.rSize.length || !sel.rSize.includes(cl.size))) return
      }
      out.push(s.code)
    })
    return out
  }, [sel, inScope, codeMeta, fwdVals, revVals, fwdSizes, revSizes])

  // ── keep parent services[] in sync with the resolved set ──────────────────────────────────
  const servicesRef = useRef(services)
  useEffect(() => { servicesRef.current = services }, [services])

  const deriveSel = (codes) => {
    const s = { dir: [], fVal: [], fLevel: [], fSize: [], rVal: [], rHand: [], rSize: [] }
    codes.forEach((code) => {
      const cl = codeMeta[code]
      if (!cl) return
      if (!s.dir.includes(cl.direction)) s.dir.push(cl.direction)
      if (cl.direction === 'FWD') {
        if (!s.fVal.includes(cl.value)) s.fVal.push(cl.value)
        if (!s.fLevel.includes(cl.level)) s.fLevel.push(cl.level)
        if (!s.fSize.includes(cl.size)) s.fSize.push(cl.size)
      } else {
        if (!s.rVal.includes(cl.value)) s.rVal.push(cl.value)
        if (!s.rHand.includes(cl.handling)) s.rHand.push(cl.handling)
        if (!s.rSize.includes(cl.size)) s.rSize.push(cl.size)
      }
    })
    return s
  }

  const didInit = useRef(false)
  useEffect(() => {
    if (didInit.current || !inScope.length) return
    didInit.current = true
    setSel(deriveSel(servicesRef.current))
  }, [inScope]) // eslint-disable-line react-hooks/exhaustive-deps

  const prevCard = useRef(cardType)
  useEffect(() => {
    if (prevCard.current === cardType) return
    prevCard.current = cardType
    setSel(deriveSel(servicesRef.current)) // blended↔cpwise carries the list; sddndd/hb clears it
  }, [cardType]) // eslint-disable-line react-hooks/exhaustive-deps

  const lastSynced = useRef(null)
  const firstSync = useRef(true)
  useEffect(() => {
    if (!didInit.current) return
    const key = resolved.slice().sort().join('|')
    if (firstSync.current) { firstSync.current = false; lastSynced.current = key; return }
    if (key === lastSynced.current) return
    lastSynced.current = key
    const want = new Set(resolved)
    const universe = new Set(inScope.map((s) => s.code))
    resolved.forEach((c) => { if (!servicesRef.current.includes(c)) onToggle(c) })
    servicesRef.current.forEach((c) => { if (!want.has(c) && universe.has(c)) onToggle(c) })
  }, [resolved, inScope]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <LinearProgress sx={{ my: 1 }} />
  if (isError || !inScope.length) {
    return (
      <Alert
        severity={isError ? 'warning' : 'info'}
        sx={{ fontSize: 12 }}
        action={isError ? <Button color="inherit" size="small" sx={{ textTransform: 'none' }} onClick={() => refetch()}>Retry</Button> : null}
      >
        {isError
          ? 'Couldn’t load the service types. Your login may not have serviceability access (this list is admin-gated today) — sign in as an admin, or the backend needs to open /service-types to D2C roles.'
          : 'No D2C service types available in the serviceability master right now.'}
      </Alert>
    )
  }

  const toggleDir = (d) => setSel((s) => {
    const dir = tog(s.dir, d)
    if (d === 'FWD' && !dir.includes('FWD')) return { ...s, dir, fVal: [], fLevel: [], fSize: [] }
    if (d === 'REV' && !dir.includes('REV')) return { ...s, dir, rVal: [], rHand: [], rSize: [] }
    return { ...s, dir }
  })

  // Body of a direction lane — just the pill rows (value / level|handling / size). The numbered
  // step wrapper is supplied by the guided-funnel render below.
  const laneBody = (dir) => {
    const isF = dir === 'FWD'
    const vals = isF ? fwdVals : revVals
    const valSel = isF ? sel.fVal : sel.rVal
    const setVal = (v) => setSel((s) => (isF ? { ...s, fVal: tog(s.fVal, v) } : { ...s, rVal: tog(s.rVal, v) }))
    // levels / handlings available for the chosen value(s) in this direction
    const valFilter = (c) => (valSel.length ? valSel.includes(c.value) : true)
    const axisLabel = isF ? 'Service level' : 'Handling'
    const axisSel = isF ? sel.fLevel : sel.rHand
    const setAxis = (v) => setSel((s) => (isF ? { ...s, fLevel: tog(s.fLevel, v) } : { ...s, rHand: tog(s.rHand, v) }))
    const axisOpts = uniq(inScope
      .filter((s) => codeMeta[s.code].direction === dir && valFilter(codeMeta[s.code]))
      .map((s) => (isF ? codeMeta[s.code].level : codeMeta[s.code].handling)))
    const sizes = isF ? fwdSizes : revSizes
    const sizeSel = isF ? sel.fSize : sel.rSize
    const setSize = (v) => setSel((s) => (isF ? { ...s, fSize: tog(s.fSize, v) } : { ...s, rSize: tog(s.rSize, v) }))

    const valEnabled = (v) => anyTransactable(codesWhere((c) => c.direction === dir && c.value === v))
    const axisEnabled = (v) => anyTransactable(codesWhere((c) => c.direction === dir && valFilter(c) && (isF ? c.level : c.handling) === v))
    const sizeEnabled = (v) => anyTransactable(codesWhere((c) => c.direction === dir && valFilter(c) && (axisSel.length ? axisSel.includes(isF ? c.level : c.handling) : true) && c.size === v))

    return (
      <>
        {vals.length > 1 && (
          <Box sx={{ mb: 1.75 }}>
            <Typography sx={grpLbl}>Consignment value</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {vals.map((v) => <FunnelChip key={v} label={VALUE_LABEL[v] || v} meta={v === 'HV' ? 'insured · OTP' : 'carrier default cover'} on={valSel.includes(v)} disabled={!valEnabled(v)} onClick={() => setVal(v)} />)}
            </Stack>
          </Box>
        )}
        <Box sx={{ mb: sizes.length > 1 ? 1.75 : 0 }}>
          <Typography sx={grpLbl}>{axisLabel}</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {axisOpts.map((v) => { const en = axisEnabled(v); return <FunnelChip key={v} label={v} meta={en ? null : 'no couriers yet'} on={axisSel.includes(v)} disabled={!en} onClick={() => setAxis(v)} /> })}
          </Stack>
        </Box>
        {sizes.length > 1 && (
          <Box>
            <Typography sx={grpLbl}>Parcel size</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {sizes.map((v) => { const en = sizeEnabled(v); return <FunnelChip key={v} label={SIZE_LABEL[v] || v} meta={en ? null : 'n/a for this level'} on={sizeSel.includes(v)} disabled={!en} onClick={() => setSize(v)} /> })}
            </Stack>
          </Box>
        )}
      </>
    )
  }

  const dirCount = (d) => codesWhere((c) => c.direction === d).length
  const laneCount = (dir) => resolved.filter((c) => codeMeta[c].direction === dir).length

  // One row of the guided funnel — a numbered node on a vertical rail + its content.
  const stepRow = (num, { last = false, done = false, label, children }) => (
    <Box sx={{ display: 'flex', gap: 1.75, pb: last ? 0 : 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0, bgcolor: done ? 'primary.main' : '#C7D0E0', color: '#fff' }}>{num}</Box>
        {!last && <Box sx={{ width: 2, flex: 1, minHeight: 16, my: 0.75, borderRadius: 2, bgcolor: done ? 'primary.main' : 'divider' }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.4 }}>
        {label}
        {children}
      </Box>
    </Box>
  )

  const dirs = ['FWD', 'REV'].filter((d) => sel.dir.includes(d) && dirCount(d) > 0)

  return (
    <Box>
      {/* Guided funnel (Option C) — a numbered 1→2→3 rail on a dashed "builder" canvas, so the
          step-by-step narrowing reads clearly as a multi-select builder, not a pick-one grid. */}
      <Box sx={{ background: 'linear-gradient(180deg, #FBFCFE, #F1F6FE)', border: '1px dashed', borderColor: '#BBD3F5', borderRadius: '14px', p: 2 }}>
        {stepRow('1', {
          done: sel.dir.length > 0,
          label: <Typography sx={grpLbl}>Direction <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>· pick one or both</Typography></Typography>,
          children: (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {dirCount('FWD') > 0 && <FunnelChip label="Forward" meta={`${dirCount('FWD')} codes`} on={sel.dir.includes('FWD')} onClick={() => toggleDir('FWD')} />}
              {dirCount('REV') > 0 && <FunnelChip label="Reverse" meta={`${dirCount('REV')} codes`} on={sel.dir.includes('REV')} onClick={() => toggleDir('REV')} />}
            </Stack>
          ),
        })}

        {dirs.map((dir, i) => stepRow(String(i + 2), {
          done: laneCount(dir) > 0,
          label: (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.85 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.dark' }}>{dir === 'FWD' ? 'Forward' : 'Reverse'}</Typography>
              <Pill label={`${laneCount(dir)} code${laneCount(dir) === 1 ? '' : 's'}`} styleKey="neutral" sx={laneCount(dir) ? { bgcolor: '#EAF1FC', color: '#1D4ED8' } : undefined} />
            </Stack>
          ),
          children: laneBody(dir),
        }))}

        {stepRow(<CheckIcon sx={{ fontSize: 17 }} />, {
          last: true,
          done: resolved.length > 0,
          label: <Typography sx={grpLbl}>Resolves to <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>· {resolved.length} code{resolved.length === 1 ? '' : 's'}</Typography></Typography>,
          children: resolved.length
            ? <Stack direction="row" flexWrap="wrap" gap={0.75}>{resolved.map((c) => <Pill key={c} label={`${codeMeta[c].name} · ${codeMeta[c].couriers} couriers`} styleKey="neutral" />)}</Stack>
            : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{sel.dir.length ? 'Pick a service level above to resolve the matching codes.' : 'Start by choosing a direction.'}</Typography>,
        })}
      </Box>

      {showErr && !resolved.length && (
        <Typography sx={{ fontSize: 12, color: 'error.main', mt: 1.2 }}>Select at least one service — pick a direction, then a service level/handling.</Typography>
      )}
    </Box>
  )
}
